import React, { useEffect, useMemo, useState, useRef } from 'react';
import Drawer from '../common/Drawer';
import SaveCancelButtons from '../common/buttons/SaveCancelButtons';
import RadioButtons from '../common/buttons/RadioButtons';
import { styled } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { showAlert } from '../../data/stores/alertStore';
import Autocomplete, { AutocompleteOption } from '../common/form/AutoComplete';
import { AppDispatch, RootState } from '../../data/store';
import { fetchGoogleUsers } from '../../services/company-service';
import { ConvertUserDTO, GoogleUser, UserDTO } from '../../data/models/user';
import useUsersList from '../../hooks/useUsersList';
import { fetchUserGroups } from '../../data/thunks/userGroupThunk';
import { UserGroup } from '../../data/models/userGroup';
import { AsyncStage } from '../../data/reduxCommon';
import Modal from '../common/Modal';
import { CompanyExternalUser } from '../../data/models/companyExternalUser';
import ModalHeader from '../common/ModalHeader';

interface ConvertCrewModalProps {
	open: boolean;
	handleClose: () => void;
	handleSave: (user: ConvertUserDTO) => void;
	user: CompanyExternalUser | undefined;
}

type CrewType = 'INTERNAL' | 'EXTERNAL' | '';
type EmployeeStatus = 'EMPLOYEE' | 'NOT_EMPLOYEE' | '';

export default function ConvertCrewModal({
	open,
	handleClose,
	user,
	handleSave,
}: ConvertCrewModalProps) {
	const dispatch = useDispatch<AppDispatch>();
	const { list: users } = useUsersList(open);
	const { userGroups } = useSelector(
		(state: RootState) => state.userGroupState
	);
	const { stage } = useSelector((state: RootState) => state.companyState);
	const { company } = useSelector((state: RootState) => state.companyState);
	const isLoadingGroupsRef = useRef(false);

	const [googleUsers, setGoogleUsers] = useState<GoogleUser[]>([]);
	const [selectedGoogleUser, setSelectedGoogleUser] =
		useState<AutocompleteOption | null>(null);

	useEffect(() => {
		const fetchGoogleUsersAsync = async () => {
			const response = await fetchGoogleUsers();
			setGoogleUsers(response);
		};
		if (open) {
			fetchGoogleUsersAsync();
		}
	}, [open]);

	// Fetch user groups when modal opens
	useEffect(() => {
		if (
			open &&
			company?.id &&
			!userGroups.initialized &&
			!isLoadingGroupsRef.current
		) {
			isLoadingGroupsRef.current = true;
			dispatch(
				fetchUserGroups({ companyId: company.id, take: 1000 })
			).finally(() => {
				isLoadingGroupsRef.current = false;
			});
		}
	}, [open, company?.id, dispatch, userGroups.initialized]);

	const userDropdownOptions = useMemo(() => {
		const usersIds = users.map((user) => user.googleId);
		const availableGoogleUsers = googleUsers.filter(
			(googleUser) => !usersIds.includes(googleUser.id)
		);

		return availableGoogleUsers.map((user) => ({
			id: user.id,
			display: user.primaryEmail,
		}));
	}, [googleUsers, users]);

	const handleConvert = () => {
		if (!selectedGoogleUser) {
			dispatch(
				showAlert({
					message: 'Please select a Google user.',
					severity: 'error',
				})
			);
			return;
		}

		const googleUser = googleUsers.find(
			(u) => u.id === selectedGoogleUser.id
		);

		if (!googleUser) {
			dispatch(
				showAlert({
					message: 'An error occurred creating the user.',
					severity: 'error',
				})
			);
			return;
		}

		const userToCreate: ConvertUserDTO = {
			companyExternalUserId: user?.id || '',
			googleId: googleUser.id,
			googleEmail: googleUser.primaryEmail,
		};

		handleSave(userToCreate);
	};

	return (
		<Modal open={open} handleClose={handleClose}>
			<ModalHeader>Convert Crew Member</ModalHeader>
			<FormContainer>
				<UserDisplay>
					{user?.firstName} {user?.lastName}
				</UserDisplay>

				<Autocomplete
					id="google-user"
					label="Google User"
					value={selectedGoogleUser}
					options={userDropdownOptions}
					onChange={(e, value) => {
						setSelectedGoogleUser(value);
					}}
				/>

				{/* <Note>
					Converting {user?.firstName} {user?.lastName} to an internal
					user will charge your company an additional $80 per month.
				</Note> */}

				<ActionButtonsContainer>
					<SaveCancelButtons
						handleSave={handleConvert}
						handleCancel={handleClose}
						saveText="CONVERT"
						saveDisabled={
							!selectedGoogleUser || stage === AsyncStage.Busy
						}
						cancelDisabled={stage === AsyncStage.Busy}
					/>
				</ActionButtonsContainer>
			</FormContainer>
		</Modal>
	);
}

const FormContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${(props) => props.theme.spacing(2)};
`;

const ActionButtonsContainer = styled.div`
	margin-top: ${(props) => props.theme.spacing(4)};
	text-align: right;
`;

const Note = styled.div`
	color: ${({ theme }) => theme.palette.text.secondary};
	font-size: 14px;
	line-height: 1.5;
	margin-bottom: ${(props) => props.theme.spacing(2)};
`;

const UserDisplay = styled.div`
	font-size: 16px;
	font-weight: 600;
	margin-bottom: ${(props) => props.theme.spacing(2)};
`;
